import { observable, action, toJS, computed, makeObservable, runInAction } from 'mobx'
import completedDingSoundSrc from '../assets/audios/happyDing.wav'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import React from 'react'

import { fetchEventSource } from '@microsoft/fetch-event-source'
import api, { baseURL as coreBaseURL, headers as getApiHeaders } from '../service/api'
import clickSoundSrc from '../assets/audios/ClickSound.wav'
// const notification = NotificationStore

const sampleRes = {
    "success": true,
    "data": {
        "user_info": {
            "name": "Sumit Singha Chowdhury",
            "user_id": "443",
            "email": "sumit2432@acharya.ac.in",
            "institute": "AIT",
            "role": "FACULTY",
            "image_path": "2026-03-10/443/1773117455700-Sumit_002_11zon.png",
            "department_name": "Master of Computer Applications",
            "department_id": 12,
            "empcode": "AI00441"
        },
        "practice_session_id": "697c4d45bc675af8d18975a1",
        "name": "Sumit Singha Chowdhury",
        "user_id": "443",
        "attempt": 1,
        "total_marks": 5,
        "questions": [
            {
                "question": "Which of the following situations violates First Normal Form (1NF)?",
                "answers": [
                    "A table where an attribute contains a list of values separated by commas",
                    "A table where every attribute contains a single, indivisible value",
                    "A table with repeating groups of columns for the same information",
                    "A table where all columns are atomic"
                ],
                "correct_answers": [
                    "A table where an attribute contains a list of values separated by commas",
                    "A table with repeating groups of columns for the same information"
                ],
                "explanation": "1NF requires all attributes to be atomic, meaning each field contains only one value. Storing lists or repeating groups in a single column or across multiple columns violates this rule."
            },
            {
                "question": "Which of the following are necessary for a table to be in First Normal Form (1NF)? Select all that apply.",
                "answers": [
                    "Each row must have a unique identifier",
                    "Each attribute must contain only atomic values",
                    "There must be no repeating groups or arrays in any column",
                    "All data must be numeric"
                ],
                "correct_answers": [
                    "Each attribute must contain only atomic values",
                    "There must be no repeating groups or arrays in any column"
                ],
                "explanation": "For 1NF, all attributes must be atomic (no arrays, lists, or sets) and there must be no repeating groups. Uniqueness of rows is handled by keys, but is not strictly a 1NF requirement. Data type (numeric or not) is irrelevant."
            },
            {
                "question": "A student table has the following columns: StudentID, Name, PhoneNumbers (which stores multiple phone numbers as a single string separated by semicolons). What is the main normalization issue here?",
                "answers": [
                    "StudentID is not unique",
                    "There are non-atomic values in a column",
                    "There are missing values",
                    "There are duplicate rows"
                ],
                "correct_answers": [
                    "There are non-atomic values in a column"
                ],
                "explanation": "Storing multiple phone numbers in a single column as a semicolon-separated string violates 1NF, which requires columns to have atomic values."
            },
            {
                "question": "Consider this table: Orders(OrderID, ProductID, ProductName1, ProductName2, ProductName3). Which normalization problem is present?",
                "answers": [
                    "Repeating groups violate 1NF",
                    "ProductIDs are not unique",
                    "OrderID is missing",
                    "All columns are atomic"
                ],
                "correct_answers": [
                    "Repeating groups violate 1NF"
                ],
                "explanation": "Having multiple columns for similar data (ProductName1, ProductName2, etc.) is a repeating group, which is not allowed in 1NF."
            },
            {
                "question": "Which of the following statements about 1NF are correct? Select all that apply.",
                "answers": [
                    "Every table in 1NF has only atomic attributes",
                    "A table in 1NF cannot have multi-valued attributes",
                    "To achieve 1NF, composite attributes must be split into separate columns",
                    "1NF ensures elimination of all data redundancy"
                ],
                "correct_answers": [
                    "Every table in 1NF has only atomic attributes",
                    "A table in 1NF cannot have multi-valued attributes",
                    "To achieve 1NF, composite attributes must be split into separate columns"
                ],
                "explanation": "1NF requires all attributes to be atomic (single-valued) and not multi-valued or composite. However, 1NF alone does not eliminate all data redundancy; higher normal forms address further redundancy."
            }
        ],
        "user_answers": [
            null,
            null,
            null,
            null,
            null
        ]
    }
}

class SimbaAIStore {

    practiceSessionQuestions
    topicsAvailableSubjects
    latestPracticeStreak
    referenceMaterials
    practiceSessions
    aiResponsesQueue
    suggestedTopics
    currentTopic
    aiResponses
    aiResponse
    viewState
    errors

    constructor () {
        this.practiceSessionQuestions = []
        this.topicsAvailableSubjects = []
        this.latestPracticeStreak = {}
        this.referenceMaterials = []
        this.practiceSessions = []
        this.aiResponsesQueue = []
        this.suggestedTopics = {
            topics: [],
            topicsCategory: ""
        }
        this.practiceHistory = []
        this.currentTopic = null
        this.leaderboard = {}
        this.aiResponses = []
        this.aiResponse = ""
        this.sounds = {
            click: new Audio( clickSoundSrc ),
            practiceCompleted: new Audio( completedDingSoundSrc )
        }
        this.viewState = {
            current: "home",
            previous: "home",
            slideDirection: "left",
        }
        this.errors = {
            referenceMaterials: true,
            practiceSessions: null,
            practiceSession: true,
            startingPractice: true,
            latestPracticeStreak: true,
            aiResponse: null,
            history: true,
            leaderboard: {}
        }
        this.loadingStatuses = {
            leaderboard: { initialLoad: false, refreshing: false }
        }
        this.quizState = {
            questionIndex: 0,
            quickOptions: [],
            conversationId: null,
            previousContent: '',
            userAnswers: [],
            attemptDetails: {}
        }

        this.questionStream = {
            status: "idle", // idle | streaming | completed | error
            expectedTotal: 0,
            error: null
        }
        this.questionStreamController = null

        for ( const sound of Object.values( this.sounds ) ) {
            try {
                sound.preload = "auto"
                sound.load()
            } catch ( err ) { }
        }

        makeObservable( this, {
            practiceSessionQuestions: observable,
            questionStream: observable,
            topicsAvailableSubjects: observable,
            latestPracticeStreak: observable,
            referenceMaterials: observable,
            practiceSessions: observable,
            practiceHistory: observable,
            loadingStatuses: observable,
            suggestedTopics: observable,
            currentTopic: observable,
            aiResponses: observable,
            leaderboard: observable,
            aiResponse: observable,
            quizState: observable,
            viewState: observable,
            errors: observable,

            fetchPracticeSessionQuestions: action,
            cancelQuestionStream: action,
            resumePendingPracticeSession: action,
            fetchPracticeSessionDetails: action,
            setTopicsAvailableSubjects: action,
            fetchLatestPracticeStreak: action,
            fetchReferenceMaterials: action,
            setActiveAttemptDetails: action,
            fetchPracticeSessions: action,
            setPracticeQuestions: action,
            setAiResponsesQueue: action,
            updatePracticeData: action,
            setSuggestedTopics: action,
            fetchUserHistory: action,
            fetchLeaderboard: action,
            getAIResponse: action,
            setQuizState: action,
            addResponse: action,
            resetValues: action,
            playSound: action,
            setView: action,

            getLeaderboardLoadingStatus: computed,
            getQuestionStream: computed,
            getPracticeSessionQuestions: computed,
            getTopicsAvailableSubjects: computed,
            getLatestPracticeStreak: computed,
            getReferenceMaterials: computed,
            getPracticeSessions: computed,
            getAiResponsesQueue: computed,
            getLeaderboardData: computed,
            getPracticeHistory: computed,
            getCurrentTopic: computed,
            getAiResponses: computed,
            getAiResponse: computed,
            getQuizState: computed,
            getErrors: computed,
            getView: computed

        } )
    }

    resetValues () {
        this.cancelQuestionStream()
        runInAction( () => {
            this.quizState = {
                questionIndex: 0,
                quickOptions: [],
                conversationId: null,
                previousContent: '',
                userAnswers: [],
                attemptDetails: {}
            }
            this.practiceSessionQuestions = []
        } )
    }

    setSuggestedTopics ( topics, category ) {
        runInAction( () => {
            this.suggestedTopics.topics = topics
            this.suggestedTopics.topicsCategory = category
        } )
    }

    addResponse ( response ) {
        runInAction( () => {
            this.aiResponses.push( response )
        } )
    }

    setAiResponsesQueue ( data ) {
        runInAction( () => {
            for ( const key in data ) {
                if ( !Object.hasOwn( data, key ) ) continue
                this.aiResponsesQueue[key] = data[key]
            }
        } )
    }

    async fetchUserHistory () {
        try {
            const { data: { data: history } } = await api.simbaAI.fetchUserPracticeHistory()
            runInAction( () => {
                this.practiceHistory = history
                this.errors.history = null
            } )
            return true
        } catch ( err ) {
            console.log( err )
            runInAction( () => {
                this.errors.history = { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong file fetching practice history." }
            } )
            return false
        }
    }

    async updatePracticeData ( payload ) {
        try {
            const { data: { data: attemptDetails } } = await api.simbaAI.updatePractice( this.quizState.practiceQuizDetails.practice_session_id, this.quizState.conversationId, payload )
            runInAction( () => {
                this.quizState.attemptDetails = attemptDetails
            } )
            return true
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Failed to submit your answers! Please try again." )
            return false
        }
    }


    async fetchReferenceMaterials () {
        try {
            const { data: { data } } = await api.simbaAI.fetchReferenceMaterials()
            runInAction( () => {
                this.referenceMaterials = data
                this.errors.referenceMaterials = null
            } )
            return { succes: true, data }
        } catch ( err ) {
            console.log( err )
            runInAction( () => {
                this.errors.referenceMaterials = { code: err?.response?.status || 500, succes: false, message: err?.response?.data?.message || "Someting went wrong, please try again." }
            } )

        }
    }

    async createPracticeSession ( payload ) {
        try {
            const { data: { data } } = await api.simbaAI.createPracticeSession( payload )
            await this.fetchPracticeSessions()
            return { success: true, data }
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while creating practice session." )
            runInAction( () => {
                this.errors.referenceMaterials = { code: err?.response?.status || 500, succes: false, message: err?.response?.data?.message || "Someting went wrong, please try again." }
            } )
            return { success: false }
        }
    }

    async fetchPracticeSessions () {
        try {
            const { data: { data } } = await api.simbaAI.fetchPracticeSessions()
            runInAction( () => {
                this.practiceSessions = data
                this.errors.practiceSessions = null
            } )
        } catch ( err ) {
            console.log( err )
            runInAction( () => {
                this.errors.practiceSessions = { code: err?.response?.status || 500, succes: false, message: err?.response?.data?.message || "Someting went wrong, please try again." }
            } )
        }
    }

    async fetchLatestPracticeStreak () {
        try {
            const { data: { data } } = await api.simbaAI.fetchLatestPracticeStreak()
            runInAction( () => {
                this.latestPracticeStreak = data
            } )
        } catch ( err ) {
            console.log( err )
            runInAction( () => {
                this.errors.latestPracticeStreak = { code: err?.response?.status || 500, succes: false, message: err?.response?.data?.message || "Someting went wrong, please try again." }
            } )
        }
    }

    cancelQuestionStream () {
        if ( this.questionStreamController ) {
            this.questionStreamController.abort()
            this.questionStreamController = null
        }
        runInAction( () => {
            this.questionStream = { status: "idle", expectedTotal: 0, error: null }
        } )
    }

    // Resolves true as soon as the first question arrives (so the quiz can start) and
    // false if generation fails before any question. Remaining questions keep streaming into the store.
    fetchPracticeSessionQuestions ( practiceSessionId ) {
        this.cancelQuestionStream()
        const controller = new AbortController()
        this.questionStreamController = controller
        const isStale = () => this.questionStreamController !== controller

        runInAction( () => {
            this.practiceSessionQuestions = []
            this.quizState.conversationId = null
            this.quizState.userAnswers = []
            this.quizState.questionIndex = 0
            this.questionStream = { status: "streaming", expectedTotal: 0, error: null }
        } )

        return new Promise( resolve => {
            let settled = false
            const settle = value => {
                if ( settled ) return
                settled = true
                resolve( value )
            }

            const fail = message => {
                if ( isStale() ) return settle( false )
                runInAction( () => {
                    this.questionStream.status = "error"
                    this.questionStream.error = message
                    this.errors.startingPractice = message
                } )
                // Nothing shown yet -> show the error view like the old flow did
                if ( !settled ) {
                    this.setView( "practice-session-details-fetch-error" )
                    settle( false )
                }
            }

            const handleEvent = ( event, data ) => {
                if ( isStale() ) return
                switch ( event ) {
                    case "start":
                        runInAction( () => {
                            this.questionStream.expectedTotal = data.total_questions || 0
                            this.quizState.userAnswers = new Array( data.total_questions || 0 ).fill( null )
                        } )
                        break

                    case "question":
                        runInAction( () => {
                            this.practiceSessionQuestions.push( data )
                        } )
                        console.log( data )

                        settle( true )
                        break

                    case "complete":
                        runInAction( () => {
                            const attempt = data.data
                            // Keep the answers the user already picked while the rest was streaming
                            const userAnswers = attempt.user_answers.map( ( answer, i ) => this.quizState.userAnswers[i] ?? answer )
                            this.practiceSessionQuestions = attempt.questions
                            this.quizState.conversationId = attempt._id
                            this.quizState.attemptDetails = attempt
                            this.quizState.userAnswers = userAnswers
                            this.questionStream = { status: "completed", expectedTotal: attempt.questions.length, error: null }
                        } )
                        settle( true )
                        break

                    case "error":
                        fail( data.message || "Something went wrong while generating the questions!" )
                        break

                    default:
                        break
                }
            }

            const readStream = async () => {
                try {
                    // const { data: { data } } = await api.simbaAI.startNewPracticeSession( practiceSessionId )
                    await fetchEventSource( `${coreBaseURL}/simba-ai/practice-sessions/${practiceSessionId}/start`, {
                        method: "POST",
                        body: JSON.stringify( {} ),
                        headers: { ...getApiHeaders(), Accept: "text/event-stream" },
                        signal: controller.signal,
                        // Keep the connection when the tab is hidden, otherwise the library reconnects
                        // and the backend generates (and counts) a brand new quiz
                        openWhenHidden: true,

                        onopen: async ( response ) => {
                            const contentType = response.headers.get( "content-type" ) || ""
                            if ( response.ok && contentType.includes( "text/event-stream" ) ) return

                            // Validation / limit errors still come back as plain JSON
                            let body = {}
                            try { body = await response.json() } catch { }
                            throw new Error( body.message || "Something went wrong while fetching practice session details!" )
                        },

                        onmessage: ( message ) => {
                            if ( !message.data ) return
                            let parsed
                            try { parsed = JSON.parse( message.data ) } catch { return }
                            handleEvent( message.event, parsed )
                        },

                        onclose: () => {
                            if ( !isStale() && this.questionStream.status === "streaming" )
                                throw new Error( "Question generation stopped unexpectedly! Please try again." )
                        },

                        // Re-throwing stops the library's automatic retry
                        onerror: ( err ) => {
                            throw err
                        }
                    } )
                } catch ( err ) {
                    if ( controller.signal.aborted ) return settle( false )
                    console.log( err )
                    fail( err?.message || "Something went wrong while generating the questions!" )
                } finally {
                    if ( !isStale() ) this.questionStreamController = null
                    // Aborted streams resolve silently; make sure the caller is never left waiting
                    if ( controller.signal.aborted ) settle( false )
                }
            }

            readStream()
        } )
    }

    async fetchPracticeSessionDetails ( id ) {
        try {
            this.resetValues()
            const { data: { data } } = await api.simbaAI.fetchPracticeSessionDetails( id )
            runInAction( () => {
                this.quizState.practiceQuizDetails = data
                this.quizState.attemptDetails = data.latest_attempt
            } )
            return { success: true, data }
        } catch ( err ) {
            console.log( err )

            runInAction( () => {
                this.errors.startingPractice = err?.response?.data?.message || "Something went wrong while fetching practice session details!"
                this.setView( "practice-session-details-fetch-error" )
            } )
            return { success: false }
        }
    }

    async fetchLeaderboard ( options ) {

        let { month } = options
        if ( !month ) {
            month = dayjs().format( "YYYY-MM" )
        }

        const loader = this.loadingStatuses.leaderboard

        try {

            runInAction( () => {
                loader.initialLoad = !this.leaderboard[month]
                loader.refreshing = true
            } )

            const { data: { data } } = await api.simbaAI.fetchLeaderboard( options )
            runInAction( () => {
                this.leaderboard[month] = data
                this.errors.leaderboard[month] = null
            } )

        } catch ( err ) {
            runInAction( () => {
                this.leaderboard[month] = []
                this.errors.leaderboard[month] = { message: err?.response?.data?.message || "Failed to fetch leaderboard details!" }
            } )
        } finally {
            runInAction( () => {
                loader.initialLoad = false
                loader.refreshing = false
            } )
        }
    }

    setActiveAttemptDetails ( practiceAttempts ) {
        runInAction( () => {
            this.quizState.attemptDetails = practiceAttempts
            this.setView( "answers" )
        } )
    }

    setPracticeQuestions ( question ) {
        this.cancelQuestionStream()
        runInAction( () => {
            this.practiceSessionQuestions = question
        } )
    }

    resumePendingPracticeSession () {
        this.cancelQuestionStream()
        runInAction( () => {

            let qIndex = this.quizState.practiceQuizDetails.incomplete_practice_quiz.user_answers.filter( c => Boolean( c ) ).length
            this.practiceSessionQuestions = this.quizState.practiceQuizDetails.incomplete_practice_quiz.questions

            if ( qIndex >= this.practiceSessionQuestions.length ) {
                qIndex = this.practiceSessionQuestions.length - 1
            }

            this.quizState.conversationId = this.quizState.practiceQuizDetails.incomplete_session_id
            this.quizState.userAnswers = this.quizState.practiceQuizDetails.incomplete_practice_quiz.user_answers
            this.quizState.attemptDetails = this.quizState.practiceQuizDetails.incomplete_practice_quiz
            this.quizState.questionIndex = qIndex
        } )
    }

    setTopicsAvailableSubjects ( subject ) {
        runInAction( () => {
            this.topicsAvailableSubjects = subject
        } )
    }

    async getAIResponse ( topicDetails ) {
        try {
            const { data: { data } } = await api.aimentor.askAiMentor( { subject: topicDetails.subject_name, topic: topicDetails.module, subtopic: topicDetails.topic } )

            let cleanedResponse = data.response
            if ( cleanedResponse?.match( /^([\n\s]*```markdown)/ ) ) {
                cleanedResponse = cleanedResponse?.replace( /^([\n\s]*```markdown)/, "" )?.replace( /(```[\n\s]*)$/, "" )
            }

            runInAction( () => {
                this.currentTopic = topicDetails
                this.errors.aiResponse = null
                this.aiResponse = cleanedResponse
                this.aiResponsesQueue = { request: topicDetails, response: cleanedResponse }
            } )
            return true
        } catch ( err ) {
            runInAction( () => {
                if ( err?.response?.data?.limitsReached ) {
                    this.setView( 'limits-reached' )
                }
                this.currentTopic = topicDetails
                this.errors.aiResponse = err?.response?.data?.message || "Something went wrong while generating your response! please try again."
                this.aiResponse = ""
            } )
            return false
        }
    }

    setView ( newView, slideDirection = "left", playSound = true ) {
        runInAction( () => {
            this.viewState.previous = this.viewState.current
            this.viewState.current = newView
            this.viewState.slideDirection = slideDirection
        } )
        if ( playSound )
            this.playSound( "click" )
    }

    setQuizState ( updates ) {
        runInAction( () => {
            for ( const key in updates ) {
                if ( !Object.hasOwn( updates, key ) ) continue
                this.quizState[key] = updates[key]
            }
        } )
    }

    playSound ( soundId ) {
        const sound = this.sounds[soundId]
        if ( !sound ) return

        if ( !sound.paused && !sound.ended ) return

        sound.currentTime = 0
        sound.volume = 1
        sound.play().catch( err => console.log( "Autoplay blocked:", err ) )
    }

    get getSuggestedTopics () {
        return toJS( this.suggestedTopics )
    }

    get getAiResponse () {
        return toJS( this.aiResponse )
    }

    get getCurrentTopic () {
        return toJS( this.currentTopic )
    }

    get getAiResponses () {
        return toJS( this.aiResponses )
    }

    get getAiResponsesQueue () {
        return toJS( this.aiResponsesQueue )
    }

    get getTopicsAvailableSubjects () {
        return toJS( this.topicsAvailableSubjects )
    }

    get getLatestPracticeStreak () {
        return toJS( this.latestPracticeStreak )
    }

    get getPracticeHistory () {
        return toJS( this.practiceHistory )
    }

    get getQuizState () {
        return toJS( this.quizState )
    }

    get getQuestionStream () {
        return toJS( this.questionStream )
    }

    get getPracticeSessionQuestions () {
        return toJS( this.practiceSessionQuestions )
    }

    get getReferenceMaterials () {
        return toJS( this.referenceMaterials )
    }

    get getPracticeSessions () {
        return toJS( this.practiceSessions )
    }

    get getLeaderboardData () {
        return toJS( this.leaderboard )
    }

    get getErrors () {
        return toJS( this.errors )
    }

    get getView () {
        return toJS( this.viewState )
    }

    // Loading statuses
    get getLeaderboardLoadingStatus () {
        return toJS( this.loadingStatuses.leaderboard )
    }

}


const SimbaAIStoreInstance = new SimbaAIStore()

export default SimbaAIStoreInstance

const SimbaAIStoreContext = React.createContext()

export const SimbaAIStoreProvider = ( { children, store } ) => {
    return (
        <SimbaAIStoreContext.Provider value={store}>{children}</SimbaAIStoreContext.Provider>
    )
}

export const useSimbaAIStore = () => React.useContext( SimbaAIStoreContext )